import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@common/components/Box';
import IconButton from '@common/components/controls/IconButton';
import Flag from '@common/components/Flag';
import { rotateIcon } from '@common/components/Icon';
import Menu, { type MenuProps } from '@common/components/Menu';
import MenuSelectOption from '@common/components/Menu/MenuSelectOption';
import IconArrowTriangleDown from '@common/components/svgs/common/ArrowTriangleDown';
import IconSpinner from '@common/components/svgs/common/Spinner';
import { fallbackLng } from '@common/i18n/constants';
import { supportedLanguages } from '@common/i18n/supportedLocales';

interface LanguageSwitcherProps {
  languages: Record<string, string>;
  align: MenuProps['align'];
}

function LanguageSwitcher({
  languages,
  align,
}: Readonly<LanguageSwitcherProps>) {
  const [loadingLanguage, setLoadingLanguage] = useState<string | undefined>();
  const { i18n: { changeLanguage, resolvedLanguage = fallbackLng } } = useTranslation();
  const selectedLanguageName = useMemo(
    () => languages[resolvedLanguage],
    [languages, resolvedLanguage],
  );

  const switchLanguage = useCallback(async (lng: string) => {
    setLoadingLanguage(languages[lng]);
    await changeLanguage(lng);
    setLoadingLanguage(undefined);
  }, [changeLanguage, languages]);

  if (!selectedLanguageName) {
    return null;
  }

  return (
    <Menu
      align={align}
      renderSelector={(selectorRef, toggleMenu, isOpen) => (
        <div ref={selectorRef}>
          <Box pl={3}>
            <span className="page-header__language">
              {loadingLanguage
                ? <IconSpinner />
                : <Flag language={resolvedLanguage} />}
              <span className="page-header__username">
                <strong className="txt-small">
                  {loadingLanguage || selectedLanguageName}
                  {/* NOSONAR:S6606 - false/undefined should show name */}
                </strong>
              </span>
              <IconButton
                icon={isOpen ? rotateIcon(IconArrowTriangleDown, 180) : IconArrowTriangleDown}
                onClick={toggleMenu}
                disabled={!!loadingLanguage}
              />
            </span>
          </Box>
        </div>
      )}
    >
      {supportedLanguages.map((language) => {
        const languageName = languages[language];
        if (!languageName) {
          return null;
        }
        return (
          <MenuSelectOption
            key={language}
            id={language}
            value={language}
            selected={resolvedLanguage}
            onClick={switchLanguage}
            data-testid={`${language}-menuselect`}
          >
            <span className="page-header__language">
              <Flag language={language} />
              <span className="menu__option-text txt-small" data-name={languageName}>
                {languageName}
              </span>
            </span>
          </MenuSelectOption>
        );
      })}
    </Menu>
  );
}

export default LanguageSwitcher;
